import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, TrendingUp, CreditCard, ArrowDownCircle, ArrowUpCircle,
  CheckCircle2, XCircle, Mail, Wallet, Trophy, Loader2, RefreshCw, PowerOff,
  ListOrdered, X as XIcon, Undo2, Percent,
} from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const fmtMoney = (v) => `$${Math.round(v || 0).toLocaleString("de-CH")}`;
const fmtInt = (v) => (v ?? 0).toLocaleString("de-CH");

export default function AdminOverview() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;
  const headers = useMemo(() => ({ "X-Admin-Token": token || "" }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [token, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/overview`, { headers });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Übersicht nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const [resetBusy, setResetBusy] = useState(false);
  const resetExcept = async () => {
    const raw = window.prompt(
      "MyFxBook-Verbindung von ALLEN Kunden trennen — ausser folgende E-Mails.\n\n" +
      "Bitte E-Mails komma-getrennt eintragen (die BEHALTEN werden sollen):",
      "1001ps95@gmail.com",
    );
    if (raw == null) return;
    const list = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      if (!window.confirm("Keine E-Mail zum Behalten eingetragen — wirklich ALLE Kunden trennen?")) return;
    }
    // Dry-run first.
    setResetBusy(true);
    try {
      const dry = await axios.post(
        `${API}/admin/myfxbook/reset-except`,
        { except_emails: list, dry_run: true },
        { headers },
      );
      const wd = dry.data.would_disconnect;
      const kept = dry.data.kept;
      if (!window.confirm(
        `Dry-Run: ${wd} Kunden würden getrennt, ${kept} Kunden bleiben verbunden.\n\n` +
        `E-Mails behalten: ${list.join(", ") || "(keine)"}\n\n` +
        `Wirklich JETZT ausführen?`,
      )) return;
      const live = await axios.post(
        `${API}/admin/myfxbook/reset-except`,
        { except_emails: list, dry_run: false },
        { headers },
      );
      toast.success(
        `${live.data.customer_accounts_updated} Kunden getrennt · ` +
        `${live.data.trade_snapshots_deleted} Snapshots · ${live.data.trades_deleted} Trades entfernt`,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setResetBusy(false);
    }
  };

  const [copyTradingBusy, setCopyTradingBusy] = useState(false);
  const applyCopyTradingFees = async () => {
    if (!window.confirm(
      "Copy-Trading-Kunden auf 5% Performance-Fee setzen?\n\n" +
      "Alle Dashboards werden mit der hinterlegten 5%-Kundenliste abgeglichen (MT5-Nummer, " +
      "E-Mail UND Name — Vor-/Nachname dürfen vertauscht sein) und auf fee_rate_override = 0.05 " +
      "markiert. Diese Kunden sehen im Dashboard 95% Netto-Profit (statt Standard 60%). " +
      "Idempotent – kann wiederholt aufgerufen werden.",
    )) return;
    setCopyTradingBusy(true);
    try {
      const r = await axios.post(`${API}/admin/customers/apply-copy-trading-fees`, {}, { headers });
      const d = r.data;
      const unmatchedTxt = [
        d.unmatched_names?.length ? `Noch kein Dashboard: ${d.unmatched_names.join(", ")}` : "",
      ].filter(Boolean).join(" · ");
      toast.success(
        `${d.matched_count} Kunden markiert · ${d.total_flagged_5pct} insgesamt auf 5%.` +
        (unmatchedTxt ? `\n${unmatchedTxt}` : ""),
        { duration: 10000 },
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCopyTradingBusy(false);
    }
  };

  const counts = data?.counts || {};
  const volume = data?.volume || {};
  const top = data?.top_customers || [];
  const connectRate = counts.total ? Math.round((counts.connected / counts.total) * 100) : 0;

  // ---- Cash-Flow-Modal ----
  const [cfCustomer, setCfCustomer] = useState(null);
  const [cfData, setCfData] = useState(null);
  const [cfLoading, setCfLoading] = useState(false);
  const [cfBusyKey, setCfBusyKey] = useState(null);
  const [manualFee, setManualFee] = useState("");
  const [manualFeeSaving, setManualFeeSaving] = useState(false);

  const loadCashFlow = async (customerId) => {
    setCfLoading(true);
    try {
      const r = await axios.get(`${API}/admin/customer/${customerId}/cash-flow`, { headers });
      setCfData(r.data);
      setManualFee(r.data.manual_fee_total != null ? String(r.data.manual_fee_total) : "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cash-Flow konnte nicht geladen werden");
    } finally {
      setCfLoading(false);
    }
  };

  const saveManualFee = async (amount) => {
    setManualFeeSaving(true);
    try {
      await axios.post(`${API}/admin/customer/${cfCustomer.id}/fee-total`, { amount }, { headers });
      toast.success(amount == null ? "Manuelle Fee entfernt — zurück auf Auto" : `Performance-Fee gesamt: $${amount}`);
      await loadCashFlow(cfCustomer.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setManualFeeSaving(false);
    }
  };
  const openCashFlow = (c) => { setCfCustomer(c); setCfData(null); loadCashFlow(c.id); };
  const closeCashFlow = () => { setCfCustomer(null); setCfData(null); };

  const overrideEntry = async (entry, isFee) => {
    setCfBusyKey(entry.key);
    try {
      // Wenn der neue Wert der Auto-Klassifikation entspricht → Override entfernen.
      const payload = isFee === entry.auto_is_fee ? { key: entry.key, is_fee: null } : { key: entry.key, is_fee: isFee };
      await axios.post(`${API}/admin/customer/${cfCustomer.id}/cash-flow/override`, payload, { headers });
      toast.success(isFee ? "Als Performance-Fee markiert" : "Als Kundenauszahlung markiert");
      await loadCashFlow(cfCustomer.id);
      await load(); // Overview-Tabelle (Fee-Spalte) aktualisieren
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCfBusyKey(null);
    }
  };
  const resetEntry = async (entry) => {
    setCfBusyKey(entry.key);
    try {
      await axios.post(`${API}/admin/customer/${cfCustomer.id}/cash-flow/override`, { key: entry.key, is_fee: null }, { headers });
      toast.success("Zurück auf automatische Klassifikation");
      await loadCashFlow(cfCustomer.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCfBusyKey(null);
    }
  };

  // Pagination for the connected-customers table (15 per page).
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(top.length / PAGE_SIZE));
  const pageStart = page * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, top.length);
  const pageRows = top.slice(pageStart, pageEnd);
  useEffect(() => { setPage(0); }, [top.length]);

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="admin-overview-page">
      <AdminTopNav />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-8">

        <header className="flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Admin</div>
            <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">Kunden-Übersicht</h1>
            <p className="mt-2 text-sm text-slate-500 max-w-2xl">
              Alle Kennzahlen deiner Kunden auf einen Blick. Demo-Konten sind ausgeschlossen; Volumen/Profit beziehen sich ausschliesslich auf Kunden mit aktiver MyFxBook-Verbindung.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={applyCopyTradingFees}
              disabled={loading || copyTradingBusy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 text-sm font-display font-semibold disabled:opacity-50 transition-all"
              data-testid="overview-apply-copy-trading"
              title="Setzt die hinterlegten Copy-Trading-Kunden auf 5% Performance-Fee (idempotent)"
            >
              {copyTradingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              5%-Kunden markieren
            </button>
            <button
              onClick={resetExcept}
              disabled={loading || resetBusy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-display font-semibold disabled:opacity-50 transition-all"
              data-testid="overview-reset-except"
              title="Trennt MyFxBook-Verbindung bei allen Kunden ausser den angegebenen E-Mails"
            >
              {resetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PowerOff className="h-4 w-4" />}
              Verbindungen zurücksetzen
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="al-cta-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2"
              data-testid="overview-refresh"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Aktualisieren
            </button>
          </div>
        </header>

        {loading && !data ? (
          <div className="al-card p-12 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Übersicht …
          </div>
        ) : (
          <>
            {/* Row 1 — Volume KPIs (the big picture) */}
            <section>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">Volumen (nur verbundene Kunden, ohne Demo)</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  icon={<CreditCard className="h-5 w-5" />}
                  label="Kundengesamtvolumen"
                  value={fmtMoney(volume.total_balance)}
                  hint={`Aktueller Kontostand aller ${counts.connected ?? 0} verbundenen Kunden`}
                  testid="overview-total-balance"
                  highlight
                />
                <StatCard
                  icon={<TrendingUp className="h-5 w-5" />}
                  label="Live-Equity"
                  value={fmtMoney(volume.total_equity)}
                  hint="Inkl. offener P&L"
                  testid="overview-total-equity"
                />
                <StatCard
                  icon={<Wallet className="h-5 w-5" />}
                  label="Ø je verbundenem Kunden"
                  value={counts.connected ? fmtMoney(volume.total_balance / counts.connected) : "—"}
                  hint="Durchschnittliche Balance"
                  testid="overview-avg-balance"
                />
                <StatCard
                  icon={<Trophy className="h-5 w-5" />}
                  label="Realisierter Profit"
                  value={fmtMoney(volume.total_realized_profit)}
                  hint="Aus geschlossenen Trades verbundener Kunden"
                  testid="overview-realized-profit"
                  valueClass={volume.total_realized_profit >= 0 ? "text-emerald-600" : "text-rose-600"}
                />
              </div>
            </section>

            {/* Row 2 — Deposits / Withdrawals split */}
            <section>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">Kapitalfluss</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <StatCard
                  icon={<ArrowDownCircle className="h-5 w-5 text-emerald-600" />}
                  label="Einzahlungen gesamt"
                  value={fmtMoney(volume.total_deposits)}
                  hint="Aggregiert von MyFxBook"
                  testid="overview-deposits"
                />
                <StatCard
                  icon={<ArrowUpCircle className="h-5 w-5 text-rose-600" />}
                  label="Kundenauszahlungen"
                  value={fmtMoney(volume.total_customer_withdrawals)}
                  hint="Eigenauszahlungen der Kunden"
                  testid="overview-customer-withdrawals"
                />
                <StatCard
                  icon={<Percent className="h-5 w-5 text-violet-600" />}
                  label="Performance-Fees"
                  value={fmtMoney(volume.total_performance_fees)}
                  hint="Abgerechnete Fees (alle Kunden)"
                  testid="overview-performance-fees"
                />
              </div>
            </section>

            {/* Row 3 — Customer count & activation KPIs */}
            <section>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">Kunden-Aktivierung</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  icon={<Users className="h-5 w-5" />}
                  label="Kunden gesamt"
                  value={fmtInt(counts.total)}
                  hint={`${counts.approved || 0} approved · ${counts.pending || 0} pending`}
                  testid="overview-total-customers"
                />
                <StatCard
                  icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                  label="MyFxBook verbunden"
                  value={`${fmtInt(counts.connected)} / ${fmtInt(counts.total)}`}
                  hint={`Aktivierungsquote: ${connectRate} %`}
                  testid="overview-connected"
                  highlight={connectRate >= 50}
                />
                <StatCard
                  icon={<XCircle className="h-5 w-5 text-amber-600" />}
                  label="Noch nicht verbunden"
                  value={fmtInt(counts.not_connected)}
                  hint="Erinnerungs-Mail sinnvoll"
                  testid="overview-not-connected"
                />
                <StatCard
                  icon={<Mail className="h-5 w-5 text-violet-600" />}
                  label="Launch-Mail versandt"
                  value={fmtInt(counts.launch_mail_sent)}
                  hint={counts.total ? `${Math.round(((counts.launch_mail_sent || 0) / counts.total) * 100)} % der Kunden` : ""}
                  testid="overview-launch-mail-sent"
                />
              </div>
            </section>

            {/* Row 4 — Top 5 customers */}
            <section className="al-card overflow-hidden" data-testid="overview-customers-section">
              <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-violet-50/40 to-transparent flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                    <Trophy className="h-3.5 w-3.5 text-[var(--al-purple)]" /> Verbundene Kunden nach Balance
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Alle verbundenen Kunden (ohne Demo), sortiert nach Balance. Seitenweise Anzeige à 15 Einträge.
                  </p>
                </div>
                {top.length > 0 && (
                  <div className="text-xs text-slate-500 tabular-nums" data-testid="overview-customers-range">
                    Zeige <strong className="text-slate-900">{pageStart + 1}–{pageEnd}</strong> von <strong className="text-slate-900">{top.length}</strong>
                  </div>
                )}
              </div>
              {top.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  Keine Kunden mit Balance &gt; $0 vorhanden.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto" data-testid="overview-customers-scroll">
                  <table className="w-full min-w-[720px] text-sm" data-testid="overview-customers-table">
                    <thead className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50/50">
                      <tr>
                        <th className="text-left px-6 py-3">#</th>
                        <th className="text-left px-6 py-3">Kunde</th>
                        <th className="text-right px-6 py-3">Einzahlungen</th>
                        <th className="text-right px-6 py-3">Auszahlungen</th>
                        <th className="text-right px-6 py-3">Perf-Fee</th>
                        <th className="text-right px-6 py-3">Balance</th>
                        <th className="text-right px-6 py-3">Equity</th>
                        <th className="px-3 py-3"></th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((c, i) => (
                        <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                          <td className="px-6 py-3 text-slate-400 font-mono-tab">{pageStart + i + 1}</td>
                          <td className="px-6 py-3">
                            <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
                              {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                              {c.fee_rate === 0.05 && (
                                <span
                                  data-testid={`fee-badge-5pct-${c.id}`}
                                  title="Copy-Trading-Kunde: 5% Performance-Fee"
                                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wider"
                                >
                                  5%
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 font-mono">{c.email}</div>
                          </td>
                          <td className="px-6 py-3 text-right font-mono-tab text-emerald-700">{fmtMoney(c.deposits_total)}</td>
                          <td className="px-6 py-3 text-right font-mono-tab text-rose-700">{fmtMoney(c.withdrawals_customer_total ?? c.withdrawals_total)}</td>
                          <td className="px-6 py-3 text-right font-mono-tab text-violet-700" data-testid={`overview-row-fee-${c.id}`}>{fmtMoney(c.performance_fees_total || 0)}</td>
                          <td className="px-6 py-3 text-right font-mono-tab font-bold">{fmtMoney(c.balance)}</td>
                          <td className="px-6 py-3 text-right font-mono-tab text-slate-600">{fmtMoney(c.equity)}</td>
                          <td className="px-3 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => openCashFlow(c)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-violet-200 bg-violet-50 hover:bg-violet-100 text-xs font-semibold text-violet-700"
                              data-testid={`overview-cashflow-btn-${c.id}`}
                            >
                              <ListOrdered className="h-3.5 w-3.5" /> Cash-Flow
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-testid="overview-customers-pager">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="overview-customers-prev"
                      >
                        ← Zurück
                      </button>
                      <div className="text-xs text-slate-600 tabular-nums">
                        Seite <strong>{page + 1}</strong> von <strong>{totalPages}</strong>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="overview-customers-next"
                      >
                        Weiter →
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}

        {/* ---- Cash-Flow-Modal (Pilot: Fee-Klassifikation prüfen/überschreiben) ---- */}
        {cfCustomer && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={closeCashFlow}
            data-testid="cashflow-modal-overlay"
          >
            <div
              className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="cashflow-modal"
            >
              <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Cash-Flow &amp; Fee-Klassifikation</div>
                  <div className="font-display font-bold text-lg text-slate-900">
                    {cfData?.customer?.name || [cfCustomer.first_name, cfCustomer.last_name].filter(Boolean).join(" ") || cfCustomer.email}
                  </div>
                  <div className="text-xs text-slate-500 font-mono">{cfCustomer.email}</div>
                </div>
                <button type="button" onClick={closeCashFlow} className="p-2 rounded-full hover:bg-slate-100" data-testid="cashflow-modal-close">
                  <XIcon className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {cfLoading || !cfData ? (
                <div className="p-10 flex items-center justify-center text-slate-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <>
                  {/* Manuelle Performance-Fee-Gesamtsumme */}
                  <div className="px-6 py-3 border-b border-slate-100 bg-violet-50/40">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-violet-700 mb-1.5">Performance-Fee gesamt (manuell)</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualFee}
                        onChange={(e) => setManualFee(e.target.value)}
                        placeholder="z.B. 71.00"
                        className="w-32 px-3 py-1.5 rounded-lg border border-violet-200 bg-white text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-violet-300"
                        data-testid="cashflow-manual-fee-input"
                      />
                      <button
                        type="button"
                        disabled={manualFeeSaving || manualFee === ""}
                        onClick={() => saveManualFee(parseFloat(manualFee))}
                        className="px-3 py-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-40"
                        data-testid="cashflow-manual-fee-save"
                      >
                        Speichern
                      </button>
                      {cfData.manual_fee_total != null && (
                        <button
                          type="button"
                          disabled={manualFeeSaving}
                          onClick={() => saveManualFee(null)}
                          className="px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-600 disabled:opacity-40"
                          data-testid="cashflow-manual-fee-clear"
                        >
                          Entfernen (Auto)
                        </button>
                      )}
                      {cfData.manual_fee_total != null && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100">manuell aktiv</span>
                      )}
                    </div>
                    <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                      Gesamtbetrag aller Performance-Fees ab Kontoverbindung. Die Differenz zu den Gesamtauszahlungen wird automatisch als Kundenauszahlung gewertet.
                    </p>
                  </div>
                  <div className="px-6 py-3 grid grid-cols-3 gap-3 border-b border-slate-100 bg-slate-50/50 text-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Einzahlungen</div>
                      <div className="font-mono-tab font-bold text-emerald-700" data-testid="cashflow-total-deposits">${(cfData.totals?.deposits ?? 0).toLocaleString("de-CH")}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kundenauszahlungen</div>
                      <div className="font-mono-tab font-bold text-rose-700" data-testid="cashflow-total-withdrawals">${(cfData.totals?.customer_withdrawals ?? 0).toLocaleString("de-CH")}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Performance-Fees</div>
                      <div className="font-mono-tab font-bold text-violet-700" data-testid="cashflow-total-fees">${(cfData.totals?.performance_fees ?? 0).toLocaleString("de-CH")}</div>
                    </div>
                  </div>

                  <div className="overflow-y-auto flex-1 divide-y divide-slate-100" data-testid="cashflow-entries">
                    {(cfData.entries || []).length === 0 && (
                      <div className="p-8 text-center text-sm text-slate-500">Keine Cash-Flow-Einträge vorhanden.</div>
                    )}
                    {(cfData.entries || []).map((e) => (
                      <div key={e.key} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`cashflow-entry-${e.key}`}>
                        <div className="w-24 text-xs text-slate-500 font-mono">
                          {e.date ? new Date(e.date).toLocaleDateString("de-CH") : "ohne Datum"}
                        </div>
                        <div className={`font-mono-tab font-semibold w-24 text-right ${e.kind === "deposit" ? "text-emerald-700" : "text-slate-900"}`}>
                          {e.amount >= 0 ? "+" : "−"}${Math.abs(e.amount).toLocaleString("de-CH", { minimumFractionDigits: 2 })}
                        </div>
                        <div className="flex-1 min-w-[130px]">
                          {e.kind === "deposit" ? (
                            <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">Einzahlung</span>
                          ) : e.is_fee ? (
                            <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-100">Performance-Fee</span>
                          ) : (
                            <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-100">Kundenauszahlung</span>
                          )}
                          {e.overridden && (
                            <span className="ml-1.5 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100">manuell</span>
                          )}
                        </div>
                        {e.kind === "withdrawal" && (
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              disabled={cfBusyKey === e.key}
                              onClick={() => overrideEntry(e, !e.is_fee)}
                              className="px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50 text-[11px] font-semibold text-slate-700 disabled:opacity-40"
                              data-testid={`cashflow-toggle-${e.key}`}
                            >
                              {e.is_fee ? "→ Kundenauszahlung" : "→ Performance-Fee"}
                            </button>
                            {e.overridden && (
                              <button
                                type="button"
                                disabled={cfBusyKey === e.key}
                                onClick={() => resetEntry(e)}
                                title="Zurück auf automatische Klassifikation"
                                className="p-1.5 rounded-full border border-slate-200 hover:bg-slate-50 text-slate-500 disabled:opacity-40"
                                data-testid={`cashflow-reset-${e.key}`}
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="px-6 py-3 border-t border-slate-100 text-[11px] text-slate-500 leading-snug">
                    Auto-Erkennung: Abbuchung ≈ Fee-Satz des Kunden (40% bzw. 5%) des Zyklus-Profits (7/14/28 Tage) = Performance-Fee. Im Zweifel wird Performance-Fee gewählt.
                    Änderungen wirken sofort auf das Kunden-Dashboard und überleben Re-Syncs.
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, hint, testid, highlight, valueClass = "" }) {
  return (
    <div
      className={`al-card p-5 ${highlight ? "ring-2 ring-violet-200 bg-gradient-to-br from-violet-50/60 to-white" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
        {icon} <span>{label}</span>
      </div>
      <div className={`mt-2 font-display font-bold text-3xl tracking-tight ${highlight ? "al-grad-text" : valueClass}`}>
        {value ?? "—"}
      </div>
      {hint && <div className="mt-1.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}
